import { MdLocationOn, MdEdit } from "react-icons/md";
import { IoMdClose } from "react-icons/io";
import { Order, SavedAddress } from "../utils/type";
import { useState, useCallback, useMemo } from "react";
import {
  createCustomerAddress,
  updateCustomerAddress,
  parseTaiwanAddress,
  getCreateAddressErrorMessage,
} from "../utils/mockApi";
import AddressFormSheet, { combinedAddress } from "@components/address/AddressFormSheet";
import type { AddAddressFormData } from "@components/address/AddAddressFlowSheet";
import {
  handleAPIAddressComponents,
  type AddressComponent,
} from "@components/address/API/handleAddressAPI";
import { cleanKeyword } from "@components/address/func/addressTextTool";
import { appToast } from "@components/Toast";
import L from "leaflet";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import SheetWrapper from "@components/wrapper/SheetWrapper";
import * as basicStyle from "@styles/index";

interface DeliverAddressCardProps {
  lineAccessToken?: string;
  savedAddresses: SavedAddress[];
  setOrder: React.Dispatch<React.SetStateAction<Order>>;
  setShowAddressInput: React.Dispatch<React.SetStateAction<boolean>>;
  setMapKey: React.Dispatch<React.SetStateAction<number>>;
  setSavedAddresses: React.Dispatch<React.SetStateAction<SavedAddress[]>>;
  showAddressInput: boolean;
  order: Order;
  mapKey: number;
}

const DeliverAddressCard = ({
  lineAccessToken,
  savedAddresses,
  setOrder,
  setShowAddressInput,
  setMapKey,
  setSavedAddresses,
  showAddressInput,
  order,
  mapKey,
}: DeliverAddressCardProps) => {
  // 注意：QuickOrder 的 bootstrap（getGasPriceList）flatten 了「所有」available_suppliers 的商品，
  // 沒有單一 supplier context 可對應到「這筆地址要送去哪個瓦斯行」，因此不能像 GasShop/Kiosk
  // 一樣從 useOrder store 讀 deliveryAreas 做前端提示（會誤用到不相干瓦斯行的範圍）。
  // 這裡刻意不傳 deliveryAreas（AddressFormSheet 對 undefined 視為「無範圍限制」），
  // 純粹交由後端在建立/更新地址時擋（fail-open：不擋 = 讓後端 400 訊息把關）。
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [editAddressValue, setEditAddressValue] = useState<string>("");
  const [openEditSheet, setOpenEditSheet] = useState(false);
  const [openAddSheet, setOpenAddSheet] = useState(false);
  const [editDeliverySheetState, setEditDeliverySheetState] = useState<{
    open: boolean;
    initialValues?: Partial<AddAddressFormData>;
  }>({ open: false });
  const [isFetchingEditLocation, setIsFetchingEditLocation] = useState(false);

  /** 註冊頁同流程新增地址：表單送出後呼叫 API 並加入 savedAddresses */
  const handleAddAddressFromSheet = async (data: AddAddressFormData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await createCustomerAddress({
        addressInfos: {
          city: data.city || undefined,
          site_id: data.area || undefined,
          road: data.road || undefined,
          address_number: data.streetNumber?.trim() || undefined,
          floor: data.floor || undefined,
          room: data.room || undefined,
          extra: data.otherAddress || undefined,
          latitude: data.latitude ?? undefined,
          longitude: data.longitude ?? undefined,
        },
      });

      const newId = String(res.customerAddressResult.customer_address_id);
      const displayAddress = combinedAddress({
        city: data.city || "",
        area: data.area || "",
        road: data.road || "",
        streetNumber: data.streetNumber,
        floor: data.floor,
        room: data.room,
      });
      const newAddr: SavedAddress = {
        id: newId,
        address: data.otherAddress?.trim() ? `${displayAddress} ${data.otherAddress}` : displayAddress,
        isPopular: false,
        lat: typeof data.latitude === "number" ? data.latitude : 24.7571,
        lng: typeof data.longitude === "number" ? data.longitude : 121.7536,
        floor: data.floor || undefined,
        room: data.room || undefined,
        extra: data.otherAddress?.trim() || undefined,
      };

      setSavedAddresses((prev) => [...prev, newAddr]);
      setOrder((prev) => ({ ...prev, addressId: newId }));
      setOpenAddSheet(false);
      setMapKey((prev) => prev + 1);
      appToast.success("地址已新增");
    } catch (err: any) {
      throw new Error(getCreateAddressErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  /** 與註冊相同流程編輯地址（含經緯度）：表單送出後呼叫更新 API 並回寫 savedAddresses；若後端回傳新 customer_address_id 與 address_id 則一併更新訂單與列表 */
  const handleEditDeliveryLocationFromSheet = async (data: AddAddressFormData) => {
    if (!order.addressId || isSubmitting) return;
    setIsSubmitting(true);
    const oldId = order.addressId;
    try {
      const result = await updateCustomerAddress(oldId, {
        addressInfos: {
          city: data.city || undefined,
          site_id: data.area || undefined,
          road: data.road || undefined,
          address_number: data.streetNumber?.trim() || undefined,
          floor: data.floor || undefined,
          room: data.room || undefined,
          extra: data.otherAddress || undefined,
          latitude: data.latitude ?? undefined,
          longitude: data.longitude ?? undefined,
        },
      });

      const displayAddress = combinedAddress({
        city: data.city || "",
        area: data.area || "",
        road: data.road || "",
        streetNumber: data.streetNumber,
        floor: data.floor,
        room: data.room,
      });
      const nextAddressText = data.otherAddress?.trim()
        ? `${displayAddress} ${data.otherAddress.trim()}`
        : displayAddress;

      const newId = String(result.customerAddressId);

      setSavedAddresses((prev) =>
        prev.map((a) =>
          a.id === oldId
            ? {
              ...a,
              id: newId,
              // 有則寫入新 address_id，無則清除舊的，避免送出時帶到已軟刪除的 binding
              address_id: result.addressId ?? undefined,
              address: nextAddressText,
              lat: typeof data.latitude === "number" ? data.latitude : a.lat,
              lng: typeof data.longitude === "number" ? data.longitude : a.lng,
              // 結構欄位同步更新，確保下次開啟修改時帶入最新資料
              city: data.city || undefined,
              site_id: data.area || undefined,
              road: data.road || undefined,
              address_number: data.streetNumber?.trim() || undefined,
              floor: data.floor || undefined,
              room: data.room || undefined,
              extra: data.otherAddress?.trim() || undefined,
            }
            : a,
        ),
      );

      if (newId !== oldId) {
        setOrder((prev) => ({ ...prev, addressId: newId }));
      }

      setEditDeliverySheetState({ open: false });
      setMapKey((prev) => prev + 1);
      appToast.success("送貨地點已更新");
    } catch (err: any) {
      throw new Error(getCreateAddressErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (addr: SavedAddress) => {
    setEditingAddressId(addr.id);
    setEditAddressValue(addr.address);
    setShowAddressInput(false);
    setOpenEditSheet(true);
  };

  const handleCancelEdit = () => {
    setEditingAddressId(null);
    setEditAddressValue("");
    setOpenEditSheet(false);
  };

  const handleUpdateAddress = async () => {
    const trimmed = editAddressValue.trim();
    if (!editingAddressId || !trimmed || isSubmitting) return;

    setIsSubmitting(true);
    const oldId = editingAddressId;
    try {
      const parsed = parseTaiwanAddress(trimmed);
      const result = await updateCustomerAddress(oldId, {
        addressInfos: {
          city: parsed.city,
          site_id: parsed.site_id || undefined,
          road: parsed.road || undefined,
          address_number: parsed.address_number || undefined,
          floor: parsed.floor ?? undefined,
          room: parsed.room ?? undefined,
          extra: parsed.extra,
        },
      });

      const newId = String(result.customerAddressId);
      setSavedAddresses((prev) =>
        prev.map((a) =>
          a.id === oldId
            ? {
                ...a,
                id: newId,
                address: trimmed,
                address_id: result.addressId ?? undefined,
              }
            : a
        )
      );
      if (newId !== oldId && order.addressId === oldId) {
        setOrder((prev) => ({ ...prev, addressId: newId }));
      }
      setEditingAddressId(null);
      setEditAddressValue("");
      setOpenEditSheet(false);
      setMapKey((prev) => prev + 1);
      appToast.success("地址已更新");
    } catch (err: any) {
      appToast.error(getCreateAddressErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 「修改送貨地點」：
   * 1. 優先使用 SavedAddress 上的結構欄位（city/site_id/road/address_number），直接填入表單，不需正規化 API。
   * 2. 結構欄位缺失時（例如草稿地址），fallback 到 handleAPIAddressComponents 正規化。
   * 3. 正規化失敗或無 token 時，最終 fallback 到 parseTaiwanAddress。
   * floor/room/extra 永遠優先沿用 SavedAddress 上的值。
   */
  const handleOpenEditDeliveryLocation = useCallback(async () => {
    const addr = savedAddresses.find((a) => a.id === order.addressId);
    if (!addr?.address) return;

    const baseValues: Partial<AddAddressFormData> = {
      addressId: Number(addr.id) || null,
      latitude: typeof addr.lat === "number" ? addr.lat : null,
      longitude: typeof addr.lng === "number" ? addr.lng : null,
      floor: addr.floor ?? "",
      room: addr.room ?? "",
      otherAddress: addr.extra ?? "",
    };

    // 路徑 1：SavedAddress 已有結構欄位（來自 getCustomerAddressList）→ 直接填入，不呼叫正規化 API
    const hasStructuredFields = !!(addr.city || addr.site_id || addr.road || addr.address_number);
    if (hasStructuredFields) {
      setEditDeliverySheetState({
        open: true,
        initialValues: {
          ...baseValues,
          city: addr.city || "",
          area: addr.site_id || "",
          road: addr.road || "",
          streetNumber: addr.address_number || "",
        },
      });
      return;
    }

    // 路徑 2：無結構欄位（草稿地址等）→ 呼叫正規化 API
    if (lineAccessToken) {
      setIsFetchingEditLocation(true);
      try {
        const res = await handleAPIAddressComponents([addr.address], lineAccessToken);
        if (res?.state === "success" && res.data && Array.isArray(res.data) && res.data[0]) {
          const item = res.data[0] as AddressComponent;
          setEditDeliverySheetState({
            open: true,
            initialValues: {
              ...baseValues,
              city: item.administrative_area_level_1 || "",
              area: item.administrative_area_level_2 || "",
              road: item.route || "",
              streetNumber: cleanKeyword(item.street_number || "", "號"),
            },
          });
          return;
        }
      } catch {
        // fallback to parseTaiwanAddress
      } finally {
        setIsFetchingEditLocation(false);
      }
    }

    // 路徑 3：無 token 或正規化失敗 → parseTaiwanAddress 解析合併地址字串
    const parsedFromAddress = parseTaiwanAddress(addr.address || "");
    setEditDeliverySheetState({
      open: true,
      initialValues: {
        ...baseValues,
        city: parsedFromAddress.city || "",
        area: parsedFromAddress.site_id || "",
        road: parsedFromAddress.road || "",
        streetNumber: parsedFromAddress.address_number || "",
      },
    });
  }, [lineAccessToken, order.addressId, savedAddresses]);

  /** 送貨地點圖釘：用 Leaflet Marker 綁在經緯度上，雙指縮放/拖動時自然跟著地圖移動 */
  const deliveryPinIcon = useMemo(
    () =>
      L.divIcon({
        className: "delivery-pin-icon",
        html: `
      <div style="display:flex;flex-direction:column;align-items:center;background:none;border:none;pointer-events:none;">
        <svg fill="#0281C8" width="40" height="40" viewBox="0 0 24 24" style="display:block;filter:drop-shadow(0px 2px 2px rgba(0,0,0,0.4))">
          <path d="M17.657 5.304c-3.124-3.073-8.189-3.073-11.313 0-3.124 3.074-3.124 8.057 0 11.13l5.656 5.565 5.657-5.565c3.124-3.073 3.124-8.056 0-11.13zm-5.657 8.195c-.668 0-1.295-.26-1.768-.732-.975-.975-.975-2.561 0-3.536.472-.472 1.1-.732 1.768-.732s1.296.26 1.768.732c.975.975.975 2.562 0 3.536-.472.472-1.1.732-1.768.732z"/>
        </svg>
        <span style="margin-top:4px;padding:4px 8px;background:#0281C8;color:#fff;font-size:12px;font-weight:600;border-radius:4px;white-space:nowrap;">送貨地點</span>
      </div>
    `,
    iconSize: [40, 56],
    iconAnchor: [20, 40],
  }),
    [],
  );

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="p-6 pb-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2.5 bg-blue-100 rounded-xl">
            <MdLocationOn className="text-blue-600 w-4 h-4" />
          </div>
          <h2 className="text-md font-bold text-gray-800">配送地址</h2>
        </div>

        {/* 地址選擇器 */}
        <div className="mb-3">
          <select
            value={order.addressId}
            onChange={(e) => {
              if (e.target.value === "add-new") {
                setOpenAddSheet(true);
                setShowAddressInput(false);
                setEditingAddressId(null);
                setEditAddressValue("");
              } else {
                setOrder((prev) => ({
                  ...prev,
                  addressId: e.target.value,
                }));
                setShowAddressInput(false);
                setEditingAddressId(null);
                setEditAddressValue("");
                setMapKey((prev: number) => prev + 1); // 強制重新建立地圖
              }
            }}
            className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none bg-white"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 1rem center",
            }}
          >
            {savedAddresses.map((addr) => (
              <option key={addr.id} value={addr.id}>
                {addr.address} {addr.isPopular ? "(常用)" : ""}
              </option>
            ))}
            <option value="add-new">+ 新增地址</option>
          </select>
        </div>


        {order.addressId && (
          <button
            type="button"
            onClick={handleOpenEditDeliveryLocation}
            disabled={isFetchingEditLocation}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 rounded-xl font-semibold text-sm text-blue-700 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isFetchingEditLocation ? "取得地址資訊中..." : "修改送貨地點"}
          </button>
        )}
      </div>

      {/* 地圖區域：可雙指縮放查看，縮放後定位點固定於地址不移動；單指不可拖動；點「修改送貨地點」進 modal 後才可調整 */}
      {!showAddressInput && (() => {
        const selectedAddr = savedAddresses.find((a) => a.id === order.addressId);
        const lat = selectedAddr?.lat ?? 23.5;
        const lng = selectedAddr?.lng ?? 121;
        return (
          <div>
            <div style={{ position: "relative", height: "300px", width: "100%", zIndex: 1, background: "#e5e7eb" }}>
              {/* 雙指捏合縮放查看地圖（單指不可拖動）；定位點 Marker 固定在地址經緯度上 */}
              <MapContainer
                key={`${mapKey}-${order.addressId}`}
                center={[lat, lng]}
                zoom={17}
                style={{ height: "100%", width: "100%" }}
                attributionControl={true}
                dragging={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                touchZoom={true}
                zoomControl={false}
              >
                <TileLayer
                  url="https://wmts.nlsc.gov.tw/wmts/EMAP6/default/GoogleMapsCompatible/{z}/{y}/{x}"
                  attribution='&copy; <a href="https://maps.nlsc.gov.tw/">國土測繪中心</a>'
                  maxZoom={19}
                />
                <Marker position={[lat, lng]} icon={deliveryPinIcon} />
              </MapContainer>
            </div>
            {/* 底部：Google Maps 連結 + 修改送貨地點 */}
            <div className="px-4 py-3 bg-white border-t-2 border-gray-100 flex flex-col gap-2">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 rounded-xl font-semibold text-sm text-gray-700 transition-all active:scale-95"
              >
                <MdLocationOn className="w-4 h-4" />在 Google Maps 打開
              </a>
            </div>
          </div>
        );
      })()}

      {/* 新增地址 Sheet（共用 AddressFormSheet：表單 + 地圖，無搜尋步驟） */}
      <AddressFormSheet
        isOpen={openAddSheet}
        onOpenChange={setOpenAddSheet}
        onSelect={handleAddAddressFromSheet}
      />

      {/* 修改送貨地點：共用 AddressFormSheet，帶 initialValues 預填既有地址 */}
      <AddressFormSheet
        isOpen={editDeliverySheetState.open}
        onOpenChange={(open) => {
          const nextOpen = typeof open === "function" ? open(editDeliverySheetState.open) : open;
          setEditDeliverySheetState({ open: nextOpen, ...(nextOpen ? {} : { initialValues: undefined }) });
        }}
        onSelect={handleEditDeliveryLocationFromSheet}
        title="修改送貨地點"
        initialValues={editDeliverySheetState.initialValues}
      />
    </div>
  );
};

export default DeliverAddressCard;
